// src/pages/ItemDetail.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { isFullAccess, canTransferItems } from '../permissions';
import { getItemById, createTransaction, getLocations, getWorkers, createToolRequest, adminReassignItem } from '../services/api';
import { detailUrl } from '../components/PhotoUpload';
import { formatDistanceToNow } from 'date-fns';
import JobPicker from '../components/JobPicker';
import SearchPicker from '../components/SearchPicker';
import PickedRow from '../components/PickedRow';

// Role permission helpers
// Start a transfer: Full + User access; not carpenter / laborer / view only (permissions table, 10/23)
const canTransfer     = canTransferItems;
const canCheckInOut   = (role) => ['power_user','project_manager','project_engineer','project_administrator','general_superintendent','site_supervisor','foreman','driver'].includes(role);
const canLoadTruck    = (role) => ['power_user','general_superintendent','site_supervisor','foreman','driver'].includes(role);
const canDeliverSite  = (role) => ['power_user','general_superintendent','site_supervisor','foreman','driver'].includes(role);
const canFlagRepair   = (role) => ['power_user','project_manager','project_engineer','project_administrator','general_superintendent','site_supervisor','foreman'].includes(role);
const canFlagLost     = (role) => ['power_user','project_manager','project_engineer','project_administrator','general_superintendent','site_supervisor','foreman'].includes(role);
const canConsume      = (role) => ['power_user','project_manager','project_administrator','general_superintendent','site_supervisor','foreman','driver'].includes(role);
const canRestock      = (role) => ['power_user','project_manager','project_administrator','driver'].includes(role);
// Edit / reassign items: Full access only (permissions table, 10/23)
const canEdit         = isFullAccess;
const canRequestTool  = (role) => ['power_user','project_manager','project_engineer','project_administrator','general_superintendent','site_supervisor','foreman'].includes(role);

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { worker, isAdmin } = useContext(AuthContext);
  const role = worker?.role || 'staff';

  const [item, setItem]           = useState(null);
  const [history, setHistory]     = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [pendingAction, setPendingAction]           = useState(null);
  const [selectedLocation, setSelectedLocation]     = useState(null);   // { loc, source }

  const [showQuantityPicker, setShowQuantityPicker] = useState(false);
  const [pendingQuantity, setPendingQuantity]       = useState(1);
  const [pendingBulkAction, setPendingBulkAction]   = useState(null);

  const [showRequestModal, setShowRequestModal]     = useState(false);
  const [requestLocationId, setRequestLocationId]   = useState('');
  const [requestLocation, setRequestLocation]       = useState(null);
  const [requestNotes, setRequestNotes]             = useState('');
  const [requestSubmitting, setRequestSubmitting]   = useState(false);
  const [requestSent, setRequestSent]               = useState(false);

  const [showReassignModal, setShowReassignModal]   = useState(false);
  const [workers, setWorkers]                       = useState([]);
  const [reassignWorker, setReassignWorker]         = useState('');
  const [reassignLocation, setReassignLocation]     = useState('');
  const [reassignSaving, setReassignSaving]         = useState(false);
  const [reassignPicking, setReassignPicking]       = useState(null);   // 'worker' | 'location' | null

  useEffect(() => {
    loadItem();
    loadLocations();
  }, [id]);

  const loadItem = async () => {
    try {
      setLoading(true);
      const res = await getItemById(id);
      if (!res.data || !res.data.item) { setError('Item not found'); return; }
      setItem(res.data.item);
      setHistory(res.data.history || []);
    } catch (err) {
      setError('Could not load item. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const res = await getLocations();
      setLocations(res.data.locations || []);
    } catch (err) {}
  };

  const getGPS = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { timeout: 5000 }
    );
  });

  const performAction = async (transactionType, toLocationId = null, qty = 1, pickSource = null) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const gps = await getGPS();
      await createTransaction({
        item_id:          id,
        transaction_type: transactionType,
        performed_by:     worker.worker_id,
        to_location_id:   toLocationId || null,
        quantity:         qty,
        gps_lat:          gps.lat,
        gps_lng:          gps.lng,
        job_pick_source:  pickSource,
      });
      setSuccess(getSuccessMessage(transactionType));
      setShowLocationPicker(false);
      setShowQuantityPicker(false);
      setSelectedLocation(null);
      setPendingQuantity(1);
      await loadItem();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Action failed. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const getSuccessMessage = (type) => ({
    check_out:    '✅ Checked out successfully',
    check_in:     '✅ Checked in — returned to warehouse',
    load_truck:   '✅ Loaded to truck',
    deliver_site: '✅ Delivered to site',
    flag_repair:  '🔧 Flagged for repair',
    flag_lost:    '⚠️ Flagged as lost',
    transfer:     '🔄 Transfer recorded',
    consume:      '✂️ Consumption recorded',
    restock:      '➕ Restocked successfully',
  }[type] || '✅ Done');

  const handleBulkAction = (action) => {
    setPendingBulkAction(action);
    setPendingQuantity(1);
    setShowQuantityPicker(true);
  };

  const handleQuantityConfirm = () => {
    if (['load_truck', 'deliver_site'].includes(pendingBulkAction)) {
      setShowQuantityPicker(false);
      setPendingAction(pendingBulkAction);
      setSelectedLocation(null);
      setShowLocationPicker(true);
    } else {
      performAction(pendingBulkAction, null, pendingQuantity);
    }
  };

  const handleAction = (action) => {
    if (action === 'transfer') { navigate(`/transfer/${id}`); return; }
    // Assets require a location for check_out, check_in, load_truck, deliver_site
    if (['check_out', 'load_truck', 'deliver_site', 'check_in'].includes(action)) {
      setPendingAction(action);
      setSelectedLocation(null);
      setShowLocationPicker(true);
      return;
    }
    performAction(action);
  };

  const handleLocationConfirm = () => {
    if (!selectedLocation) return;
    performAction(pendingAction, selectedLocation.loc.location_id, pendingQuantity, selectedLocation.source);
  };

  const handleRequestSubmit = async () => {
    if (!requestLocationId) return;
    setRequestSubmitting(true);
    try {
      await createToolRequest({
        item_id:                   id,
        requested_by:              worker.worker_id,
        requested_for_location_id: requestLocationId,
        notes:                     requestNotes || undefined,
      });
      setRequestSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit request.');
      setShowRequestModal(false);
    } finally {
      setRequestSubmitting(false);
    }
  };

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setRequestLocationId('');
    setRequestLocation(null);
    setRequestNotes('');
    setRequestSent(false);
  };

  const openReassignModal = async () => {
    setReassignWorker(item.current_worker_id || '');
    setReassignLocation(item.current_location_id || '');
    setReassignPicking(null);
    if (workers.length === 0) {
      try {
        const res = await getWorkers({ active: true });
        setWorkers(res.data.workers || []);
      } catch {}
    }
    setShowReassignModal(true);
  };

  const handleReassign = async () => {
    setReassignSaving(true);
    setError('');
    try {
      await adminReassignItem(id, {
        worker_id:   reassignWorker   || null,
        location_id: reassignLocation || null,
      });
      setSuccess('✅ Ownership and location updated');
      setShowReassignModal(false);
      await loadItem();
    } catch (err) {
      setError(err.response?.data?.error || 'Reassign failed.');
    } finally {
      setReassignSaving(false);
    }
  };

  const getStatusBadge = (status) => (
    <span className={`badge badge-${status}`}>{status?.replace(/_/g, ' ')}</span>
  );

  const getCategoryIcon = (code) => ({
    PWR:'🔌',HAND:'🔨',AIR:'💨',ENV:'🌡️',BATT:'🔋',SAFE:'🦺',
    TEMP:'🚧',LADR:'🪜',MEAS:'📐',CORD:'🔌',EQUIP:'⚙️',
    DWLL:'🧱',FRAM:'🏗️',FAST:'🔩',FINS:'🪣',ELEC:'⚡',
    PLMB:'🔧',PANT:'🎨',INSL:'📦',CEIL:'🏢',CHEM:'🧴',MISC:'📦'
  }[code] || '📦');

  const BackButton = () => (
    <button onClick={() => navigate(-1)}
      style={{ background:'none',border:'none',color:'#FFFFFF',cursor:'pointer',padding:'4px 8px 4px 0' }}>
      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );

  if (loading) return (
    <div className="screen">
      <div className="screen-header"><BackButton /><h2>Loading...</h2></div>
      <div className="loading"><div className="spinner" /><span>Loading item...</span></div>
    </div>
  );

  if (!item) return (
    <div className="screen">
      <div className="screen-header"><BackButton /><h2>Item Not Found</h2></div>
      <div className="screen-content">
        <div className="alert alert-error" style={{ marginBottom: 16 }}>{error || 'Item could not be loaded.'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Go to Dashboard</button>
        <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => navigate('/search')}>Search Inventory</button>
        <button className="btn btn-ghost" style={{ marginTop: 10 }} onClick={loadItem}>Try Again</button>
      </div>
    </div>
  );

  const isBulk      = item.item_type === 'bulk';
  const status      = item.current_status;
  const unit        = item.unit_of_measure || 'units';
  // True when the tool is held by someone other than the current user
  // API returns current_worker_id from i.* in the items query
  const heldByOther = !isBulk && !!(item.current_worker_id && item.current_worker_id !== worker?.worker_id);

  return (
    <div className="screen">
      <div className="screen-header">
        <BackButton />
        <div style={{ flex:1, minWidth:0 }}>
          <h2 style={{ fontSize:'1rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            {item.name}
          </h2>
          <span className="asset-tag">{item.asset_tag || item.sku || item.qr_code_id}</span>
        </div>
        {getStatusBadge(status)}
        {canEdit(role) && (
          <div style={{ display:'flex', gap:6, marginLeft:8 }}>
            <button onClick={() => navigate(`/admin/edit/${id}`)}
              style={{ background:'#FF6B6B', border:'none', color:'white', borderRadius:8,
                padding:'6px 12px', fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
                whiteSpace:'nowrap' }}>
              ✏️ Edit
            </button>
            <button onClick={openReassignModal}
              style={{ background:'#1d4ed8', border:'none', color:'white', borderRadius:8,
                padding:'6px 12px', fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
                whiteSpace:'nowrap' }}>
              ⚙️ Reassign
            </button>
          </div>
        )}
      </div>

      <div className="screen-content">

        {error   && <div className="alert alert-error"   style={{ marginBottom:12 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom:12 }}>{success}</div>}

        {/* Item info card */}
        <div className="card" style={{ marginBottom:16 }}>
          {item.photo_url && (
            <img
              src={detailUrl(item.photo_url)}
              alt={item.name}
              className="item-detail-photo"
            />
          )}
          <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
            <div style={{ fontSize:'2.5rem' }}>{getCategoryIcon(item.category_code)}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <h3>{item.name}</h3>
              <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem', marginTop:2 }}>
                {item.manufacturer_name && `${item.manufacturer_name} · `}
                {item.category_name}
                {item.battery_platform && ` · ${item.battery_platform}`}
              </p>
              {item.model_number && (
                <p style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>Model: {item.model_number}</p>
              )}
            </div>
          </div>

          <div className="divider" />

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <InfoField label="Location"   value={item.current_location_name || '—'} />
            <InfoField label="Project"    value={item.current_project_name || item.procore_project_name || '—'} />
            {!isBulk && <InfoField label="Assigned To" value={item.assigned_to_name || 'Unassigned'} />}
            {isBulk  && <InfoField label="Stock" value={`${item.available_quantity ?? 0} ${unit}`} />}
          </div>

          {(item.manufacturer_website || item.manufacturer_phone) && (
            <>
              <div className="divider" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {item.manufacturer_website && (
                  <div>
                    <p style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:2 }}>Website</p>
                    <a href={item.manufacturer_website} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize:'0.85rem', color:'var(--procore-blue)', textDecoration:'none' }}>
                      {item.manufacturer_website.replace('https://','').replace('http://','')}
                    </a>
                  </div>
                )}
                {item.manufacturer_phone && (
                  <div>
                    <p style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:2 }}>Phone</p>
                    <a href={`tel:${item.manufacturer_phone}`}
                      style={{ fontSize:'0.85rem', color:'var(--procore-blue)', textDecoration:'none' }}>
                      {item.manufacturer_phone}
                    </a>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* RESTRICTED VIEW — tool is held by someone else */}
        {heldByOther && (
          <div style={{ marginBottom: 20 }}>
            <div className="card" style={{ marginBottom: 12 }}>
              <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                Checked Out To
              </p>
              <p style={{ fontWeight: 700, fontSize: '1rem', marginBottom: item.current_location_name ? 12 : 0 }}>
                {item.assigned_to_name || '—'}
              </p>
              {item.current_location_name && (
                <>
                  <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                    Location
                  </p>
                  <p style={{ fontWeight: 600, fontSize: '0.95rem' }}>{item.current_location_name}</p>
                </>
              )}
            </div>
            {canRequestTool(role) && (status === 'checked_out' || status === 'on_site' || status === 'in_transit') && (
              <button className="btn btn-ghost" style={{ borderColor: 'var(--procore-blue)', color: 'var(--procore-blue)' }}
                onClick={() => setShowRequestModal(true)}>
                📋 Request This Tool for My Jobsite
              </button>
            )}
          </div>
        )}

        {/* FULL ACTIONS — user holds the tool, or tool is available / in repair */}
        {!heldByOther && (
          <>
            <h3 style={{ marginBottom:12 }}>Actions</h3>

            {actionLoading ? (
              <div className="loading" style={{ padding:20 }}>
                <div className="spinner" /><span>Processing...</span>
              </div>
            ) : (
              <>
                {/* Serialized asset actions */}
                {!isBulk && (
                  <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>

                    {canTransfer(role) && (status === 'checked_out' || status === 'on_site' || status === 'in_transit') && (
                      <button className="btn btn-primary" onClick={() => handleAction('transfer')}>
                        🔄 Transfer to Someone Else
                      </button>
                    )}

                    {canCheckInOut(role) && status === 'available' && (
                      <button className="btn btn-success" onClick={() => handleAction('check_out')}>
                        📤 Check Out to Me
                      </button>
                    )}

                    {canCheckInOut(role) && (status === 'checked_out' || status === 'on_site') && (
                      <button className="btn btn-blue" onClick={() => handleAction('check_in')}>
                        📥 Check In — Return to Warehouse
                      </button>
                    )}

                    {canLoadTruck(role) && (status === 'available' || status === 'checked_out') && (
                      <button className="btn btn-secondary" onClick={() => handleAction('load_truck')}>
                        🚛 Load to Truck
                      </button>
                    )}

                    {canDeliverSite(role) && status === 'in_transit' && (
                      <button className="btn btn-purple" onClick={() => handleAction('deliver_site')}>
                        📍 Confirm Delivered to Site
                      </button>
                    )}

                    {canCheckInOut(role) && status === 'in_repair' && (
                      <button className="btn btn-success" onClick={() => handleAction('check_in')}>
                        ✅ Return from Repair — Mark Available
                      </button>
                    )}

                    {(canFlagRepair(role) || canFlagLost(role)) && (
                      <div className="action-grid">
                        {canFlagRepair(role) && (
                          <button className="btn btn-warning" onClick={() => handleAction('flag_repair')}>
                            🔧 Flag for Repair
                          </button>
                        )}
                        {canFlagLost(role) && (
                          <button className="btn btn-danger" onClick={() => handleAction('flag_lost')}>
                            ⚠️ Flag as Lost
                          </button>
                        )}
                      </div>
                    )}

                    {canRequestTool(role) && (status === 'checked_out' || status === 'on_site' || status === 'in_transit') && (
                      <button className="btn btn-ghost" style={{ borderColor: 'var(--procore-blue)', color: 'var(--procore-blue)' }}
                        onClick={() => setShowRequestModal(true)}>
                        📋 Request This Tool for My Jobsite
                      </button>
                    )}
                  </div>
                )}

                {/* Bulk consumable actions */}
                {isBulk && (
                  <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
                    {canLoadTruck(role) && (
                      <button className="btn btn-primary" onClick={() => handleBulkAction('load_truck')}>
                        🚛 Load to Truck
                      </button>
                    )}
                    {canDeliverSite(role) && (
                      <button className="btn btn-purple" onClick={() => handleBulkAction('deliver_site')}>
                        📍 Deliver to Site
                      </button>
                    )}
                    {canConsume(role) && (
                      <button className="btn btn-warning" onClick={() => handleBulkAction('consume')}>
                        ✂️ Record Use / Consumption
                      </button>
                    )}
                    {canRestock(role) && (
                      <button className="btn btn-blue" onClick={() => handleBulkAction('restock')}>
                        ➕ Restock / Receive Inventory
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Transaction History */}
        {history.length > 0 && (
          <div>
            <h3 style={{ marginBottom:12 }}>Recent History</h3>
            <div className="card">
              {history.slice(0, 8).map(txn => (
                <div key={txn.transaction_id} className="item-row" style={{ cursor:'default', alignItems:'flex-start' }}>
                  <div className="item-row-icon" style={{ fontSize:'1rem', marginTop:4 }}>
                    {getTxnIcon(txn.transaction_type)}
                  </div>
                  <div className="item-row-info" style={{ flex:1 }}>
                    <h4 style={{ fontSize:'0.85rem' }}>{formatTxnType(txn.transaction_type)}</h4>
                    {txn.transaction_type === 'transfer_rejected' ? (
                      txn.performed_by && (
                        <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Rejected by: {txn.performed_by}</p>
                      )
                    ) : (
                      <>
                        {txn.performed_by && (
                          <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>By: {txn.performed_by}</p>
                        )}
                        {txn.assigned_to && (
                          <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>To: {txn.assigned_to}</p>
                        )}
                      </>
                    )}
                    {txn.to_location && (
                      <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Location: {txn.to_location}</p>
                    )}
                    {isBulk && txn.quantity && (
                      <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Qty: {txn.quantity} {unit}</p>
                    )}
                    <p style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:2 }}>
                      {txn.created_at
                        ? new Date(txn.created_at).toLocaleDateString('en-US', {
                            month:'short', day:'numeric', year:'numeric'
                          }) + ' · ' + new Date(txn.created_at).toLocaleTimeString('en-US', {
                            hour:'numeric', minute:'2-digit', hour12:true
                          })
                        : ''}
                    </p>
                    <p style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>
                      {txn.created_at
                        ? formatDistanceToNow(new Date(txn.created_at), { addSuffix:true })
                        : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button className="btn btn-ghost" style={{ marginTop:24 }} onClick={() => navigate('/')}>
          ← Back to Dashboard
        </button>

      </div>

      {/* QUANTITY PICKER MODAL */}
      {showQuantityPicker && (
        <div className="modal-overlay" onClick={() => setShowQuantityPicker(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h3 style={{ marginBottom:8 }}>
              {pendingBulkAction === 'load_truck'   ? '🚛 Load to Truck' :
               pendingBulkAction === 'deliver_site' ? '📍 Deliver to Site' :
               pendingBulkAction === 'consume'      ? '✂️ Record Use' :
               pendingBulkAction === 'restock'      ? '➕ Restock' : 'Enter Quantity'}
            </h3>
            <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', marginBottom:16 }}>
              {item.name} · {item.available_quantity} {unit} available
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
              <button onClick={() => setPendingQuantity(q => Math.max(1, q - 1))}
                style={{ width:48, height:48, borderRadius:8, border:'1px solid var(--border-light)',
                  background:'var(--bg-elevated)', color:'var(--text-primary)', fontSize:'1.5rem',
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                −
              </button>
              <div style={{ flex:1, textAlign:'center' }}>
                <input type="number" className="input" value={pendingQuantity} min={1}
                  onChange={e => setPendingQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{ textAlign:'center', fontSize:'1.5rem', fontWeight:700 }} />
                <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginTop:4 }}>{unit}</p>
              </div>
              <button onClick={() => setPendingQuantity(q => q + 1)}
                style={{ width:48, height:48, borderRadius:8, border:'1px solid var(--border-light)',
                  background:'var(--bg-elevated)', color:'var(--text-primary)', fontSize:'1.5rem',
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                +
              </button>
            </div>
            <button className="btn btn-primary" onClick={handleQuantityConfirm}
              disabled={actionLoading || pendingQuantity < 1}>
              Confirm — {pendingQuantity} {unit}
            </button>
            <button className="btn btn-ghost" style={{ marginTop:10 }} onClick={() => setShowQuantityPicker(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* TOOL REQUEST MODAL */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={closeRequestModal}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            {requestSent ? (
              <>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✅</div>
                  <h3>Request Sent</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 8 }}>
                    Admin has been notified. They'll coordinate the transfer.
                  </p>
                </div>
                <button className="btn btn-primary" onClick={closeRequestModal}>Done</button>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom: 4 }}>📋 Request This Tool</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 16 }}>
                  {item.name} · Admin will be notified to coordinate the transfer.
                </p>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Which jobsite needs this tool?
                </label>
                {requestLocation ? (
                  <PickedRow icon="📍" label={requestLocation.name}
                    onChange={() => { setRequestLocation(null); setRequestLocationId(''); }} />
                ) : (
                  <div style={{ marginBottom: 12 }}>
                    <JobPicker
                      locations={locations.filter(l => l.location_type === 'jobsite')}
                      forWorker={worker}
                      allTitle="All jobs"
                      onPick={(l) => { setRequestLocation(l); setRequestLocationId(l.location_id); }}
                    />
                  </div>
                )}
                <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Notes (optional)
                </label>
                <textarea className="input" rows={2} placeholder="e.g. Needed for rough framing starting Monday"
                  value={requestNotes} onChange={e => setRequestNotes(e.target.value)}
                  style={{ marginBottom: 16, resize: 'none' }} />
                <button className="btn btn-primary" onClick={handleRequestSubmit}
                  disabled={!requestLocationId || requestSubmitting}>
                  {requestSubmitting ? 'Sending…' : 'Send Request'}
                </button>
                <button className="btn btn-ghost" style={{ marginTop: 10 }} onClick={closeRequestModal}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ADMIN REASSIGN MODAL */}
      {showReassignModal && (
        <div className="modal-overlay" onClick={() => setShowReassignModal(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h3 style={{ marginBottom: 4 }}>⚙️ Reassign Tool</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 16 }}>
              {item.name} — admin override. Logs an adjustment to history.
            </p>

            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Owner
            </label>
            {reassignPicking === 'worker' ? (
              <div style={{ marginBottom: 14 }}>
                <button className="btn btn-ghost" style={{ marginBottom: 10 }}
                  onClick={() => { setReassignWorker(''); setReassignPicking(null); }}>
                  Unassigned (return to available)
                </button>
                <SearchPicker
                  items={workers}
                  getKey={(w) => w.worker_id}
                  getLabel={(w) => w.display_name}
                  getSub={(w) => (w.role || '').replace(/_/g, ' ')}
                  icon="👤"
                  selectedKey={reassignWorker}
                  placeholder="Type a name…"
                  allTitle="Everyone"
                  emptyText="No one found"
                  onPick={(w) => { setReassignWorker(w.worker_id); setReassignPicking(null); }}
                />
              </div>
            ) : (
              <PickedRow icon="👤"
                label={workers.find(w => w.worker_id === reassignWorker)?.display_name || (reassignWorker ? (item.assigned_to_name || 'Current owner') : 'Unassigned (return to available)')}
                onChange={() => setReassignPicking('worker')} />
            )}

            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Location
            </label>
            {reassignPicking === 'location' ? (
              <div style={{ marginBottom: 20 }}>
                <button className="btn btn-ghost" style={{ marginBottom: 10 }}
                  onClick={() => { setReassignLocation(''); setReassignPicking(null); }}>
                  No location
                </button>
                <JobPicker
                  locations={locations}
                  forWorker={workers.find(w => w.worker_id === reassignWorker) || null}
                  selectedId={reassignLocation}
                  onPick={(l) => { setReassignLocation(l.location_id); setReassignPicking(null); }}
                />
              </div>
            ) : (
              <PickedRow icon="📍"
                label={locations.find(l => l.location_id === reassignLocation)?.name || 'No location'}
                onChange={() => setReassignPicking('location')} />
            )}

            <button className="btn btn-primary" onClick={handleReassign} disabled={reassignSaving}>
              {reassignSaving ? 'Saving…' : 'Save Changes'}
            </button>
            <button className="btn btn-ghost" style={{ marginTop: 10 }}
              onClick={() => setShowReassignModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* LOCATION PICKER MODAL */}
      {showLocationPicker && (
        <div className="modal-overlay" onClick={() => { setShowLocationPicker(false); setSelectedLocation(null); }}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h3 style={{ marginBottom:16 }}>
              {pendingAction === 'check_out'    ? 'Select Job Site / Location' :
               pendingAction === 'load_truck'   ? 'Select Truck' :
               pendingAction === 'deliver_site' ? 'Select Job Site' :
               pendingAction === 'check_in'     ? 'Select Destination' :
               'Select Location'}
            </h3>
            {pendingAction === 'check_out' && (
              <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:12 }}>
                Where is this tool going? Both a location and person are required for assets.
              </p>
            )}
            {(() => {
              const choices = locations.filter(l => {
                if (pendingAction === 'check_out')    return ['jobsite','warehouse','external'].includes(l.location_type);
                if (pendingAction === 'load_truck')   return l.location_type === 'truck';
                if (pendingAction === 'deliver_site') return l.location_type === 'jobsite';
                if (pendingAction === 'check_in')     return l.location_type === 'warehouse' || l.location_type === 'truck';
                return true;
              });
              if (selectedLocation) return (
                <PickedRow icon={selectedLocation.loc.location_type === 'truck' ? '🚛' : '📍'}
                  label={selectedLocation.loc.name}
                  sub={selectedLocation.loc.procore_project_name || null}
                  onChange={() => setSelectedLocation(null)} />
              );
              // Jobs get suggestions (where you are + your Labor Board jobs);
              // trucks and the warehouse are a short searchable list.
              return ['check_out', 'deliver_site'].includes(pendingAction) ? (
                <JobPicker locations={choices} forWorker={worker}
                  onPick={(l, source) => setSelectedLocation({ loc: l, source })} />
              ) : (
                <SearchPicker
                  items={choices}
                  getKey={(l) => l.location_id}
                  getLabel={(l) => l.name}
                  getSub={(l) => (l.location_type || '').replace(/_/g, ' ')}
                  icon={pendingAction === 'load_truck' ? '🚛' : '📍'}
                  placeholder="Type a name…"
                  allTitle={pendingAction === 'load_truck' ? 'Trucks' : 'Locations'}
                  onPick={(l, source) => setSelectedLocation({ loc: l, source })}
                />
              );
            })()}
            {selectedLocation && (
              <button className="btn btn-primary" onClick={handleLocationConfirm}
                disabled={actionLoading}>
                {actionLoading ? 'Saving…' : 'Confirm'}
              </button>
            )}
            <button className="btn btn-ghost" style={{ marginTop:10 }}
              onClick={() => { setShowLocationPicker(false); setSelectedLocation(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:2 }}>{label}</p>
      <p style={{ fontSize:'0.9rem', fontWeight:500 }}>{value}</p>
    </div>
  );
}

function getTxnIcon(type) {
  return {
    check_out:'📤', check_in:'📥', transfer:'🔄', transfer_rejected:'❌', load_truck:'🚛',
    deliver_site:'📍', consume:'✂️', restock:'➕', audit:'🔍',
    flag_repair:'🔧', flag_lost:'⚠️', retire:'🗑️', adjust:'✏️',
    tool_requested:'📋', request_declined:'🚫'
  }[type] || '📋';
}

function formatTxnType(type) {
  return type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || type;
}